import { useEffect, useMemo, useState, useContext, useRef, useLayoutEffect } from 'react';
import { useCalendarApp, ScheduleXCalendar } from '@schedule-x/react';
import { createViewDay, createViewWeek, createViewMonthGrid } from '@schedule-x/calendar';
import { createEventsServicePlugin } from '@schedule-x/events-service';
import '@schedule-x/theme-default/dist/index.css';
import TimeGridEvent from '../TimeGridEvent';
import ServicePeriodLegend from '../ServicePeriodLegend/ServicePeriodLegend';
import HeaderRightActions from '../Weekly/HeaderRight/HeaderRight';
import WeekGridHour from '../Weekly/WeekGridHour';
import { ConfigContext } from '../../../../contexts/ConfigContext';
import ViewBookingModal from '../../../bookingmanagement/components/ViewBooking/ViewBookingModal';
import MonthGridEvent from '../MonthGridEvent';
import EditBookingModal from '../../../bookingmanagement/components/EditBooking/EditBookingModal';
import './bookingCalendar.css';

export default function BookingCalendar({ events, onRangeChange, view, dailyRefetch, onSearchChange }) {
  const eventsService = useState(() => createEventsServicePlugin())[0];
  const config = useContext(ConfigContext);
  const [eventPopover, setEventPopover] = useState(null);
  const popoverRef = useRef(null);
  const [popoverStyle, setPopoverStyle] = useState({
    visibility: 'hidden'
  });
  const [viewBookingShow, setViewBookingShow] = useState(false);
  const [selectedBookingId, setSelectedBookingId] = useState(null);
  const [editBookingShow, setEditBookingShow] = useState(false);
  const [selectedEditBookingId, setSelectedEditBookingId] = useState(null);

  const viewRef = useRef(view);
  const eventsRef = useRef(events);

  useEffect(() => {
    viewRef.current = view;
  }, [view]);

  useEffect(() => {
    eventsRef.current = events;
  }, [events]);

  useEffect(() => {
    const close = () => {
      setEventPopover(null);
      // setSelectedBookingId(null);
    };
    // const close = () => {
    //   setEventPopover(null);

    //   if (!viewBookingShow) {
    //     setSelectedBookingId(null);
    //   }
    // };

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') close();
    };

    window.addEventListener('click', close);
    window.addEventListener('scroll', close);
    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('click', close);
      window.removeEventListener('scroll', close);
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  useLayoutEffect(() => {
    if (!eventPopover || !popoverRef.current) return;

    let rafId = requestAnimationFrame(() => {
      const rect = popoverRef.current.getBoundingClientRect();
      const padding = 12;

      let left = eventPopover.x;
      let top = eventPopover.y;

      if (left + rect.width > window.innerWidth - padding) {
        left = eventPopover.x - rect.width - padding;
      }

      if (left < padding) {
        left = padding;
      }

      if (top + rect.height > window.innerHeight - padding) {
        top = window.innerHeight - rect.height - padding;
      }

      if (top < padding) {
        top = padding;
      }

      setPopoverStyle({
        top,
        left,
        visibility: 'visible'
      });
    });

    return () => cancelAnimationFrame(rafId);
  }, [eventPopover]);

  const calendar = useCalendarApp({
    views: [createViewDay(), createViewWeek(), createViewMonthGrid()],
    weekOptions: {
      gridStep: 30,
      gridHeight: 4000
    },
    events: [],
    plugins: [eventsService],
    isDark: config.mode === 'dark',

    callbacks: {
      fetchEvents({ start, end }) {
        onRangeChange?.({ start, end });
        return [];
      },

      onEventClick(event, domEvent) {
        const currentView = viewRef.current;

        if (event?.data?.type === 'closed') return;

        // if (currentView === 'week') {
        //   return;
        // }

        domEvent.preventDefault();
        domEvent.stopPropagation();

        if (currentView === 'day' || currentView === 'week') {
          setPopoverStyle({ visibility: 'hidden' });
          setEventPopover({
            type: currentView,
            event,
            x: domEvent.clientX + 8,
            y: domEvent.clientY
          });
          return;
        }

        setPopoverStyle({ visibility: 'hidden' });
        setEventPopover({
          type: 'month',
          event,
          x: domEvent.clientX + 8,
          y: domEvent.clientY
        });
      }
    }
  });

  useEffect(() => {
    eventsService.set(events);
  }, [events, eventsService]);

  useEffect(() => {
    if (!calendar) return;

    calendar.setTheme(config.mode === 'dark' ? 'dark' : 'light');
  }, [calendar, config.mode]);

  const openViewBookingModal = (booking) => {
    const id = booking?.bookingNo || booking?.id;
    if (!id) return;

    setSelectedBookingId(id);
    setViewBookingShow(true);
    setEventPopover(null);
  };

  const openEditBookingModal = (booking) => {
    const id = booking?.id || booking?.bookingNo;
    if (!id) return;

    setSelectedEditBookingId(id);
    setEditBookingShow(true);
    setEventPopover(null);
  };

  const customComponents = useMemo(
    () => ({
      // headerContentRightAppend: HeaderRightActions,
      headerContentRightAppend: (props) => <HeaderRightActions {...props} onSearchChange={onSearchChange} />,
      headerContentLeftPrepend: ServicePeriodLegend,
      // timeGridEvent: TimeGridEvent,

      timeGridEvent: (props) => <TimeGridEvent {...props} view={viewRef.current} />,

      weekGridHour: WeekGridHour,
      monthGridEvent: MonthGridEvent
    }),
    []
  );

  if (!calendar) return null;

  return (
    <>
      <ScheduleXCalendar key={config.mode} calendarApp={calendar} customComponents={customComponents} />

      {eventPopover && (
        <div ref={popoverRef} className="calendar-event-popover" style={popoverStyle} onClick={(e) => e.stopPropagation()}>
          {eventPopover.type === 'month' && (
            <>
              <div className="popover-header">
                <strong>
                  {eventPopover.event.data.service} ({eventPopover.event.data.count})
                </strong>
              </div>

              <div className="popover-list">
                {/* {eventPopover.event.data.bookings.map((b, idx) => (
                  <div key={idx} className="popover-row clickable" onClick={() => openViewBookingModal(b)}>
                    <span className="name">{b.customer?.name}</span>
                    <span className="time">{b.time}</span>
                  </div>
                ))} */}
                {eventPopover.event.data.bookings.map((b, idx) => {
                  const bookingId = b.bookingNo;
                  const isSelected = selectedBookingId === bookingId;
                  const today = new Date().toISOString().split('T')[0];
                  const isBookingPast = b.date && b.date < today;
                  const isCompleted = b.status === 'completed';
                  const isNoShow = b.status === 'confirmed' && isBookingPast;

                  return (
                    <div key={b.id || idx} className="popover-row">
                      <input
                        type="checkbox"
                        className="row-checkbox"
                        checked={isSelected}
                        onChange={(e) => {
                          e.stopPropagation();
                          setSelectedBookingId(e.target.checked ? bookingId : null);
                        }}
                      />

                      <div className="row-content clickable" onClick={() => openViewBookingModal(b)}>
                        <span className="name">
                          {b.customer?.name}
                          {isCompleted && (
                            <span style={{ marginLeft: 6, fontSize: 11, fontWeight: 600, color: '#198754' }}>Completed</span>
                          )}
                          {isNoShow && (
                            <span style={{ marginLeft: 6, fontSize: 11, fontWeight: 600, color: '#6c757d' }}>No Show</span>
                          )}
                        </span>
                        <span className="time">{b.time}</span>
                      </div>

                      {isSelected && (
                        <div className="row-actions">
                          <button
                            className="icon-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              openViewBookingModal(b);
                            }}
                            title="View Appointment"
                          >
                            <i className="ph ph-eye"></i>
                          </button>

                          {!isBookingPast && (
                            <button
                              className="icon-btn danger"
                              onClick={(e) => {
                                e.stopPropagation();
                                openEditBookingModal(b);
                              }}
                              title="Postpone Booking"
                            >
                              <i className="ph ph-calendar-dots"></i>
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </>
          )}

          {/* {(eventPopover.type === 'day' ||
            eventPopover.type === 'week' )&& (
              <>
                <div className="popover-header">
                  <strong>{eventPopover.event.data.customerName}</strong>
                </div>

                <div className="popover-actions">
                  <button
                    className="popover-action"
                    onClick={() => {
                      openViewBookingModal(eventPopover.event.data?.raw);
                    }}
                  >
                    View details
                  </button>

                  <button
                    className="popover-action danger"
                    onClick={() => {
                      openEditBookingModal(eventPopover.event.data?.raw);
                    }}
                  >
                    Postpone booking
                  </button>
                </div>
              </>
            ))} */}
          {['day', 'week'].includes(eventPopover?.type) && (() => {
            const bookingDate = eventPopover?.event?.data?.date;
            const bookingStatus = eventPopover?.event?.data?.raw?.booking_status;
            const today = new Date().toISOString().split('T')[0];
            const isBookingPast = (bookingDate && bookingDate < today) || bookingStatus === 'expired';
            return (
              <>
                <div className="popover-header">
                  <strong>{eventPopover?.event?.data?.customerName || 'Guest'}</strong>
                </div>

                <div className="popover-actions">
                  <button className="popover-action" onClick={() => openViewBookingModal(eventPopover?.event?.data?.raw)}>
                    View Details
                  </button>

                  {!isBookingPast && (
                    <button className="popover-action danger" onClick={() => openEditBookingModal(eventPopover?.event?.data?.raw)}>
                      Postpone Booking
                    </button>
                  )}
                </div>
              </>
            );
          })()}
        </div>
      )}

      <ViewBookingModal
        show={viewBookingShow}
        onHide={() => {
          setViewBookingShow(false);
          setSelectedEditBookingId(null);
        }}
        bookingId={selectedBookingId}
        refresh={() => {}}
      />

      <EditBookingModal
        show={editBookingShow}
        onHide={(shouldRefresh) => {
          setEditBookingShow(false);
          setSelectedEditBookingId(null);
          if (shouldRefresh) {
            dailyRefetch();
          }
        }}
        id={selectedEditBookingId}
        refresh={() => {
          dailyRefetch();
        }}
      />
    </>
  );
}
